import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import LoginModal from '@/components/LoginModal'
import { 
  MessageSquare, 
  Zap, 
  Shield, 
  BarChart3,
  CheckCircle,
  ArrowRight,
  Star,
  Users,
  Globe,
  Clock
} from 'lucide-react'

// 核心功能特性
const features = [
  {
    icon: Zap,
    title: '极速发送',
    description: '毫秒级响应，99.9%到达率保障',
    color: 'text-yellow-600'
  },
  {
    icon: Shield,
    title: '安全可靠',
    description: '企业级安全防护，数据加密传输',
    color: 'text-green-600'
  },
  {
    icon: BarChart3,
    title: '实时监控',
    description: '全链路监控，实时数据分析',
    color: 'text-blue-600'
  },
  {
    icon: Globe,
    title: '全球覆盖',
    description: '支持200+国家和地区',
    color: 'text-purple-600'
  }
]

// 客户案例
const testimonials = [
  {
    company: '某电商平台',
    industry: '电商零售',
    content: '使用SMS云平台后，我们的验证码到达率提升到99.8%，用户体验显著改善。',
    author: '技术总监 张先生',
    avatar: '🏪'
  },
  {
    company: '某金融科技',
    industry: '金融科技',
    content: '平台的安全性和稳定性让我们很放心，API接入也非常简单。',
    author: '产品经理 李女士',
    avatar: '🏦'
  },
  {
    company: '某教育机构',
    industry: '在线教育',
    content: '成本降低了30%，发送效率提升了50%，非常满意。',
    author: 'CTO 王先生',
    avatar: '🎓'
  }
]

// 统计数据
const stats = [
  { label: '企业客户', value: '10,000+', icon: Users },
  { label: '日发送量', value: '1000万+', icon: MessageSquare },
  { label: '服务可用性', value: '99.99%', icon: Clock },
  { label: '客户满意度', value: '98%', icon: Star }
]

export default function HomePage() {
  const [showLoginModal, setShowLoginModal] = useState(false)

  return (
    <div className="space-y-20">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
                  🚀 全新升级，更稳定更高效
                </Badge>
                <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  专业的
                  <span className="text-blue-600">短信云服务</span>
                  平台
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  为企业提供稳定、高效、安全的短信发送解决方案。
                  支持验证码、通知、营销等多种场景，助力业务增长。
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="text-lg px-8 py-3"
                  onClick={() => setShowLoginModal(true)}
                >
                  免费试用
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button variant="outline" size="lg" className="text-lg px-8 py-3" asChild>
                  <Link to="/docs">
                    查看文档
                  </Link>
                </Button>
              </div>
              
              <div className="flex items-center space-x-6 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span>免费试用</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span>无需信用卡</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span>5分钟接入</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="relative z-10 bg-white rounded-2xl shadow-2xl p-8">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">发送统计</h3>
                    <Badge variant="success">实时数据</Badge>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">99.8%</div>
                      <div className="text-sm text-gray-600">到达率</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">3秒</div>
                      <div className="text-sm text-gray-600">平均延迟</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">1000万+</div>
                      <div className="text-sm text-gray-600">日发送量</div>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                      <div className="text-2xl font-bold text-orange-600">24/7</div>
                      <div className="text-sm text-gray-600">技术支持</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* 装饰性背景 */}
              <div className="absolute -top-4 -right-4 w-72 h-72 bg-gradient-to-br from-blue-400 to-purple-600 rounded-full opacity-10 blur-3xl"></div>
              <div className="absolute -bottom-8 -left-8 w-64 h-64 bg-gradient-to-br from-green-400 to-blue-600 rounded-full opacity-10 blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* 核心功能 */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
            为什么选择我们
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            专业的技术团队，丰富的行业经验，为您提供最优质的短信服务
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <Card key={index} className="text-center hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className={`mx-auto w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center mb-4`}>
                  <feature.icon className={`h-6 w-6 ${feature.color}`} />
                </div>
                <CardTitle className="text-xl">{feature.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  {feature.description}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* 统计数据 */}
      <section className="bg-gray-900 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <stat.icon className="h-8 w-8 mx-auto mb-4 text-blue-400" />
                <div className="text-3xl lg:text-4xl font-bold mb-2">{stat.value}</div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 客户案例 */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
            客户案例
          </h2>
          <p className="text-xl text-gray-600">
            看看我们的客户怎么说
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="text-2xl">{testimonial.avatar}</div>
                  <div>
                    <CardTitle className="text-lg">{testimonial.company}</CardTitle>
                    <CardDescription>{testimonial.industry}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">"{testimonial.content}"</p>
                <p className="text-sm text-gray-500">— {testimonial.author}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center space-y-8">
            <h2 className="text-3xl lg:text-4xl font-bold">
              准备开始了吗？
            </h2>
            <p className="text-xl opacity-90 max-w-2xl mx-auto">
              立即注册，免费试用我们的短信服务，体验专业级的短信发送平台
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                variant="secondary" 
                className="text-lg px-8 py-3"
                onClick={() => setShowLoginModal(true)}
              >
                立即开始
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" className="text-lg px-8 py-3 border-white text-white hover:bg-white hover:text-blue-600" asChild>
                <Link to="/pricing">
                  查看价格
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* 登录 Modal */}
      <LoginModal 
        open={showLoginModal} 
        onOpenChange={setShowLoginModal} 
      />
    </div>
  )
}